/**
 * F5.3 Wave-4 — Oracle data-type vocabulary and position detection.
 *
 * Surfaces the curated Oracle type list at "column-type" / "parameter-type" /
 * "CAST target" / "RETURN type" / DECLARE slots. The grammar's clause
 * classifier returns `'unknown'` at these positions; the narrow text-walk in
 * `isOracleDataTypePosition` recognises them by surface keywords. Six shapes:
 *
 *   1. `CREATE TABLE foo (colName |`
 *   2. `ALTER TABLE foo ADD (colName |` / `MODIFY colName |`
 *   3. `CREATE [OR REPLACE] FUNCTION foo(param_name |`
 *   4. `CREATE [OR REPLACE] FUNCTION foo(...) RETURN |`
 *   5. `CAST(expr AS |)`
 *   6. `DECLARE var_name |` — PL/SQL local-variable declaration.
 *
 * Pure text-walk — relies on the shared tokenizer.
 */

import { dropAttachedPartialTokens, tokenize, type Token } from '../context/tokenizer';
import { isConstraintLeadWord } from './constraintLeadWords';

const ENGINE = 'oracle' as const;

/**
 * Curated Oracle data type vocabulary surfaced at column-type / CAST-target
 * positions. Aligns with the autocomplete coverage scripts (29 / 33 / ...).
 * Includes two-token / three-token forms (`INTERVAL DAY TO SECOND`).
 */
const ORACLE_DATA_TYPES: readonly string[] = [
  // Numeric
  'NUMBER',
  'INTEGER',
  'FLOAT',
  'BINARY_FLOAT',
  'BINARY_DOUBLE',
  'BINARY_INTEGER',
  'PLS_INTEGER',
  // Character
  'CHAR',
  'NCHAR',
  'VARCHAR2',
  'NVARCHAR2',
  'LONG',
  // Date / time
  'DATE',
  'TIMESTAMP',
  'TIMESTAMP WITH TIME ZONE',
  'TIMESTAMP WITH LOCAL TIME ZONE',
  'INTERVAL YEAR TO MONTH',
  'INTERVAL DAY TO SECOND',
  // Large objects
  'CLOB',
  'NCLOB',
  'BLOB',
  'BFILE',
  // Binary / row
  'RAW',
  'LONG RAW',
  'ROWID',
  'UROWID',
  // PL/SQL & misc
  'BOOLEAN',
  'XMLTYPE',
  // Native JSON column type (21c+; the canonical spelling in 23ai). Listed
  // unconditionally, like `BOOLEAN` above — which is likewise PL/SQL-only
  // before 23ai — because this vocabulary is not version-gated. Closes
  // oracle script.29 §1.
  'JSON',
];

export function getOracleDataTypes(): readonly string[] {
  return ORACLE_DATA_TYPES;
}

/**
 * Cursor types that are legal as a PL/SQL routine's RETURN type (and as a
 * local's type) but NOT as a table column's type — Oracle rejects
 * `CREATE TABLE t (c SYS_REFCURSOR)`. They therefore stay out of
 * `ORACLE_DATA_TYPES` and are emitted only where
 * `isOracleRoutineReturnTypeSlot` says the caret is a RETURN type.
 * Closes oracle script.43 §2.
 */
const ORACLE_CURSOR_TYPES: readonly string[] = ['SYS_REFCURSOR', 'REF CURSOR'];

export function getOracleCursorTypes(): readonly string[] {
  return ORACLE_CURSOR_TYPES;
}

/**
 * True when the caret sits at a PL/SQL routine's `RETURN ▮` type, with or
 * without a partial type already typed. Requires the routine header's `)` (or
 * the routine name, for a parameterless function) immediately before the
 * keyword and no `BEGIN` yet — the same two guards the shared slot recognizer
 * uses to tell a RETURN TYPE from a `RETURN <expr>;` statement.
 */
export function isOracleRoutineReturnTypeSlot(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  if (raw.length === 0) return false;
  const last = raw[raw.length - 1]!;
  const attached = last.end === cursor && (last.type === 'ident' || last.type === 'kw');
  const tokens = attached ? raw.slice(0, -1) : raw;
  const i = tokens.length - 1;
  if (identUpper(tokens[i]) !== 'RETURN') return false;
  if (!tokens.some((t) => identUpper(t) === 'FUNCTION')) return false;
  if (tokens.some((t) => identUpper(t) === 'BEGIN')) return false;
  const before = tokens[i - 1];
  if (!before) return false;
  return before.type === 'rparen' || before.type === 'ident';
}

/**
 * PL/SQL type-reference patterns that anchor an identifier to a table /
 * column's existing type without restating it: `<table>.<col>%TYPE`,
 * `<table>%ROWTYPE`. Only valid in PL/SQL declarations (PROCEDURE/FUNCTION
 * params, DECLARE blocks, IS|AS body local vars); NOT valid in DDL column-type
 * slots. Surfaced as suffix-style snippets so the user types the table/column
 * head and tab-accepts the pattern.
 */
const ORACLE_PLSQL_TYPE_PATTERNS: readonly string[] = ['%TYPE', '%ROWTYPE'];

export function getOraclePlsqlTypePatterns(): readonly string[] {
  return ORACLE_PLSQL_TYPE_PATTERNS;
}

/**
 * Detect whether the cursor sits at an Oracle "data-type" slot.
 *
 * TYPED-PREFIX PARITY: a bare word the caret is ATTACHED to (`RETURN NUM▮`,
 * `(p_id VARCHAR▮`) is the type being typed, not context — dropped via
 * {@link dropAttachedPartialTokens} so an attached partial classifies exactly
 * like the bare caret. The `AS` / `RETURN` arms also read the RAW tail so a
 * caret attached to the introducer keyword itself keeps firing.
 */
export function isOracleDataTypePosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  if (raw.length === 0) return false;
  const tokens = dropAttachedPartialTokens(raw, cursor);
  const prev = tokens[tokens.length - 1];
  if (!prev) return false;
  const rawPrevUpper = identUpper(raw[raw.length - 1]);

  // Shape 5: ... AS |   (CAST(expr AS |))
  if (identUpper(prev) === 'AS' || rawPrevUpper === 'AS') {
    if (isInsideCallTo(tokens, ['CAST'])) return true;
  }

  // Shape 4: `... RETURN |` — previous token is RETURN at the head of a
  // CREATE FUNCTION statement (Oracle uses `RETURN`, not `RETURNS`).
  if (identUpper(prev) === 'RETURN' || rawPrevUpper === 'RETURN') {
    if (isInsideCreateFunctionHeader(tokens)) return true;
  }

  // Shape 1 / 2 / 3 / 6 — previous token is an identifier (the column /
  // parameter / variable name) followed by a data-type slot.
  if (prev.type === 'ident' && !isConstraintLeadWord(prev.text)) {
    if (isInsideCreateTableColumnList(tokens)) return true;
    if (followsAlterTableAddColumn(tokens)) return true;
    if (isInsideCreateFunctionParamList(tokens)) return true;
    if (followsPlsqlDeclare(tokens)) return true;
  }

  // Shape 3-mode — PL/SQL procedure/function parameter mode keywords (`IN`,
  // `OUT`, `IN OUT`) sit between the param name and the type slot. When the
  // prev token is one of those modes and we are inside a CREATE
  // FUNCTION/PROCEDURE param list, treat the slot as data-type so the type
  // vocabulary surfaces. Pinned by oracle script.33 §1.
  const prevUpper = identUpper(prev);
  if (
    (prevUpper === 'IN' || prevUpper === 'OUT') &&
    isInsideCreateFunctionParamList(tokens) &&
    paramModePrecededByName(tokens)
  ) {
    return true;
  }

  return false;
}

/**
 * True at PL/SQL-only data-type slots — PROCEDURE/FUNCTION parameter
 * declarations and DECLARE blocks — where `%TYPE` / `%ROWTYPE` suffix
 * patterns are valid. False at DDL column-type / CAST / RETURN slots where
 * those PL/SQL-only suffixes would be invalid SQL. Pinned by oracle
 * script.33 §1 (the parameter slot lists `%TYPE` / `%ROWTYPE` alongside
 * scalar types).
 */
export function isOraclePlsqlDataTypePosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  if (raw.length === 0) return false;
  // TYPED-PREFIX PARITY — see `isOracleDataTypePosition`.
  const tokens = dropAttachedPartialTokens(raw, cursor);
  const prev = tokens[tokens.length - 1];
  if (!prev) return false;

  if (prev.type === 'ident') {
    if (isInsideCreateFunctionParamList(tokens)) return true;
    if (followsPlsqlDeclare(tokens)) return true;
  }
  const prevUpper = identUpper(prev);
  if (
    (prevUpper === 'IN' || prevUpper === 'OUT') &&
    isInsideCreateFunctionParamList(tokens) &&
    paramModePrecededByName(tokens)
  ) {
    return true;
  }
  return false;
}

/**
 * Walk back from the cursor past the parameter-mode keywords (`IN`, `OUT`,
 * `IN OUT`) and confirm the token before them is an identifier (the param
 * name) preceded by `(` or `,` — i.e. a fresh parameter slot. This keeps the
 * data-type emitter from misfiring at e.g. `WHERE x IN |` where `IN` is the
 * predicate keyword, not a parameter mode.
 */
function paramModePrecededByName(tokens: readonly Token[]): boolean {
  let i = tokens.length - 1;
  let sawIn = false;
  let sawOut = false;
  while (i >= 0) {
    const u = identUpper(tokens[i]);
    if (u === 'IN') {
      if (sawIn) return false;
      sawIn = true;
      i -= 1;
      continue;
    }
    if (u === 'OUT') {
      if (sawOut) return false;
      sawOut = true;
      i -= 1;
      continue;
    }
    break;
  }
  if (i < 0) return false;
  if (tokens[i]!.type !== 'ident') return false;
  const before = tokens[i - 1];
  if (!before) return false;
  return before.type === 'lparen' || before.type === 'comma';
}

// ─────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}

function findEnclosingLparenIndex(tokens: readonly Token[]): number {
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.type === 'rparen') depth += 1;
    else if (t.type === 'lparen') {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return -1;
}

function isInsideCallTo(
  tokens: readonly Token[],
  callNames: readonly string[],
): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  const before = tokens[lparenIdx - 1];
  const upper = identUpper(before);
  if (!upper) return false;
  return callNames.includes(upper);
}

function isInsideCreateTableColumnList(tokens: readonly Token[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  let sawTable = false;
  for (let i = lparenIdx - 1; i >= Math.max(0, lparenIdx - 12); i -= 1) {
    const u = identUpper(tokens[i]);
    if (u === 'TABLE') {
      sawTable = true;
      break;
    }
    const t = tokens[i]!;
    if (t.type === 'lparen' || t.type === 'rparen' || t.type === 'comma') break;
  }
  if (!sawTable) return false;
  // Validate that the prev (column-name) sits at a "fresh column slot" — i.e.
  // its previous significant token is the column-list `(` or a `,` separator.
  // Otherwise the prev is mid-clause (e.g. `REFERENCES`, `CONSTRAINT`,
  // `FOREIGN KEY`) and the data-type emitter must NOT fire.
  const beforePrev = tokens[tokens.length - 2];
  if (!beforePrev) return false;
  if (beforePrev.type === 'lparen' || beforePrev.type === 'comma') return true;
  return false;
}

/**
 * True when the cursor sits at `ALTER TABLE x ADD (colName |` (inside paren)
 * or `ALTER TABLE x MODIFY colName |` (no paren). Oracle ALTER TABLE
 * supports both forms.
 */
function followsAlterTableAddColumn(tokens: readonly Token[]): boolean {
  // Inside-paren form: ALTER TABLE … ADD ( colName |
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx > 0) {
    // Walk back from the opener looking for ADD then ALTER TABLE.
    let sawAdd = false;
    for (let i = lparenIdx - 1; i >= Math.max(0, lparenIdx - 8); i -= 1) {
      const u = identUpper(tokens[i]);
      if (u === 'ADD') {
        sawAdd = true;
        break;
      }
      const t = tokens[i]!;
      if (t.type === 'lparen' || t.type === 'rparen' || t.type === 'comma' || t.type === 'semicolon') break;
    }
    if (sawAdd) {
      // Now confirm ALTER TABLE is before the ADD.
      for (let j = lparenIdx - 1; j >= 0; j -= 1) {
        const ju = identUpper(tokens[j]);
        if (ju === 'TABLE') {
          for (let k = j - 1; k >= 0; k -= 1) {
            const ku = identUpper(tokens[k]);
            if (ku === 'ALTER') return true;
            if (tokens[k]!.type === 'semicolon') return false;
          }
        }
        if (tokens[j]!.type === 'semicolon') return false;
      }
    }
  }

  // MODIFY form: ALTER TABLE x MODIFY colName |   (no paren required)
  let sawIdent = false;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    const u = identUpper(t);
    if (t.type === 'semicolon') return false;
    if (i === tokens.length - 1) {
      // The previous token (passed to isInsideOracleDataTypePosition's caller)
      // is the identifier; treat it as seen.
      sawIdent = true;
      continue;
    }
    if (u === 'MODIFY' && sawIdent) {
      for (let j = i - 1; j >= 0; j -= 1) {
        const ju = identUpper(tokens[j]);
        if (ju === 'TABLE') {
          for (let k = j - 1; k >= 0; k -= 1) {
            const ku = identUpper(tokens[k]);
            if (ku === 'ALTER') return true;
            if (tokens[k]!.type === 'semicolon') return false;
          }
        }
        if (tokens[j]!.type === 'semicolon') return false;
      }
      return false;
    }
  }
  return false;
}

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement
 * is a `CREATE [OR REPLACE] FUNCTION` / `PROCEDURE` header.
 */
function isInsideCreateFunctionParamList(tokens: readonly Token[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx < 0) return false;
  const head = tokens.slice(0, lparenIdx);
  const upperHead = head.map((t) => identUpper(t)).filter((u): u is string => !!u);
  let i = 0;
  if (upperHead[i] !== 'CREATE') return false;
  i += 1;
  if (upperHead[i] === 'OR') {
    i += 1;
    if (upperHead[i] !== 'REPLACE') return false;
    i += 1;
  }
  return upperHead[i] === 'FUNCTION' || upperHead[i] === 'PROCEDURE';
}

/**
 * True when the cursor sits at the `RETURN |` slot of a CREATE FUNCTION
 * header (no enclosing paren, RETURN is the most recent significant keyword).
 */
function isInsideCreateFunctionHeader(tokens: readonly Token[]): boolean {
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  const upperAll = tokens
    .map((t) => identUpper(t))
    .filter((u): u is string => !!u);
  let i = 0;
  if (upperAll[i] !== 'CREATE') return false;
  i += 1;
  if (upperAll[i] === 'OR') {
    i += 1;
    if (upperAll[i] !== 'REPLACE') return false;
    i += 1;
  }
  return upperAll[i] === 'FUNCTION' || upperAll[i] === 'PROCEDURE';
}

/**
 * True when the cursor's most recent DECLARE keyword precedes the
 * current variable identifier AND no statement terminator intervenes. Used
 * to detect PL/SQL `DECLARE var_name |` blocks.
 */
function followsPlsqlDeclare(tokens: readonly Token[]): boolean {
  // F24: enclosing-paren guard. A call-argument / expression position such as
  // `proc(myvar|` sits inside an unclosed `(`. The back-walk below crosses
  // `;` and `BEGIN` to find a DECLARE, so without this guard it misclassifies
  // a call-arg slot inside a DECLARE…BEGIN block as a DECLARE data-type slot
  // (emitting NUMBER / VARCHAR2 where a column / variable ref belongs). The
  // sibling slot detectors (isInsideCreateFunctionParamList,
  // isInsideCreateTableColumnList, followsAlterTableAddColumn, isInsideCallTo)
  // all gate on the enclosing paren — do the same here: a real PL/SQL DECLARE
  // data-type slot is never inside an unclosed paren.
  if (findEnclosingLparenIndex(tokens) !== -1) return false;

  // Walk back from the cursor; if we hit a DECLARE before any semicolon /
  // BEGIN, this is a DECLARE block.
  for (let i = tokens.length - 2; i >= 0; i -= 1) {
    const t = tokens[i]!;
    const u = identUpper(t);
    if (u === 'DECLARE') return true;
    if (u === 'BEGIN' || t.type === 'semicolon') {
      // PL/SQL allows multiple DECLAREs separated by `;` — keep walking back.
      continue;
    }
    if (u === 'CREATE' || u === 'ALTER' || u === 'SELECT' || u === 'INSERT' || u === 'UPDATE' || u === 'DELETE') {
      return false;
    }
  }
  return false;
}
